import React, { useMemo } from 'react';
import {
  PlayerRecentSessionsCarousel,
  normalizeGameDerivedSession,
} from './PlayerRecentSessionsCarousel';

/**
 * Carousel preview of recent sessions for the stats / performance tab.
 * Full list lives at /profile/sessions (optionally filtered by club).
 */
const PlayerRecentSessionsList = ({ games = [], currentPlayerId, pageSize = 5, clubId = null }) => {
  const sessions = useMemo(() => {
    if (!games || games.length === 0) return [];

    const map = new Map();

    games.forEach((game) => {
      const sessionId = game.session_games?.session_id || game.session_id;
      const gameClubId = game.club_id;
      if (!sessionId || !gameClubId) return;

      const key = `${gameClubId}:${sessionId}`;
      const existing = map.get(key) || {
        sessionId,
        clubId: gameClubId,
        sessionName: game.sessionName || game.session_type_name || 'Unknown Session',
        clubName: game.clubName || game.club_name || 'Unknown Club',
        latestDate: game.created_at ? new Date(game.created_at) : null,
        gamesPlayed: 0,
        wins: 0,
        losses: 0,
        totalEloChange: 0,
      };

      const gameDate = game.created_at ? new Date(game.created_at) : null;
      if (gameDate && (!existing.latestDate || gameDate > existing.latestDate)) {
        existing.latestDate = gameDate;
      }

      existing.gamesPlayed += 1;
      if (game.result === 'win') existing.wins += 1;
      if (game.result === 'loss') existing.losses += 1;
      if (typeof game.eloChange === 'number') existing.totalEloChange += game.eloChange;

      map.set(key, existing);
    });

    return Array.from(map.values())
      .map((session) => ({
        ...session,
        winRate: session.gamesPlayed > 0
          ? Math.round((session.wins / session.gamesPlayed) * 100)
          : 0,
      }))
      .sort((a, b) => (b.latestDate || 0) - (a.latestDate || 0))
      .slice(0, pageSize)
      .map(normalizeGameDerivedSession);
  }, [games, pageSize]);

  const viewAllTo = clubId
    ? `/profile/sessions?clubId=${encodeURIComponent(clubId)}`
    : '/profile/sessions';

  return (
    <PlayerRecentSessionsCarousel
      sessions={sessions}
      currentUser={currentPlayerId ? { id: currentPlayerId } : null}
      title="My Recent Sessions"
      viewAllTo={viewAllTo}
      maxItems={pageSize}
      emptyMessage="No recent sessions found for your current filters."
    />
  );
};

export default PlayerRecentSessionsList;
